/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup,
  IconButton, MenuItem, Paper, Stack, Switch, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
  TextField, Tooltip, Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AddIcon from '@mui/icons-material/Add'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addDays, addMonths, addYears, format } from 'date-fns'
import { useSearchParams } from 'react-router-dom'
import { platformBillingAPI } from '../../api/crm'
import { getErrorMessage, getErrorList } from '../../utils/apiErrors'
import {
  CURRENCIES, FEATURE_LABELS, PHASES, formatMoney, formatNumber, formatRate, formatStorage, invoiceFileName,
} from '../../utils/billing'
import { downloadBlob } from '../../utils/helpers'
import { useNotify } from '../../components/common/NotificationProvider'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'

const STATUSES = [
  { value: 'TRIALING', label: 'Trial' },
  { value: 'ACTIVE', label: 'Active (paid)' },
  { value: 'PAST_DUE', label: 'Past due' },
  { value: 'CANCELLED', label: 'Cancelled' },
]
const PRICE_FIELDS = [
  { key: 'MONTH', label: 'Monthly' },
  { key: 'YEAR', label: 'Yearly' },
  { key: 'SEAT_MONTH', label: 'Extra member, monthly' },
  { key: 'SEAT_YEAR', label: 'Extra member, yearly' },
]
const day = (iso) => (iso ? format(new Date(iso), 'd MMM yyyy') : '—')
const toInput = (date) => format(date, "yyyy-MM-dd'T'HH:mm")

const errorText = (error) => [getErrorMessage(error), ...getErrorList(error)].join(' · ')

/** Platform admins: every workspace's subscription, and the plans on offer. */
const PlatformBillingPage = () => {
  const [params, setParams] = useSearchParams()
  const tab = ['plans', 'workspaces', 'invoicing', 'coupons'].includes(params.get('tab')) ? params.get('tab') : 'overview'
  return (
    <>
      <PageHeader title="Plans & billing" subtitle="Subscriptions of every workspace, and the plans customers can choose." />
      <Tabs value={tab} onChange={(_, v) => setParams({ tab: v }, { replace: true })} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="overview" label="Overview" />
        <Tab value="workspaces" label="Workspaces" />
        <Tab value="plans" label="Plans" />
        <Tab value="invoicing" label="Invoicing" />
        <Tab value="coupons" label="Coupons" />
      </Tabs>
      {tab === 'plans' ? <PlansTab /> : tab === 'workspaces' ? <WorkspacesTab />
        : tab === 'invoicing' ? <InvoicingTab /> : tab === 'coupons' ? <CouponsTab /> : <OverviewTab />}
    </>
  )
}

// ------------------------------------------------------------------ overview

const moneyList = (byCurrency) => {
  const entries = Object.entries(byCurrency || {})
  return entries.length ? entries.map(([c, v]) => formatMoney(v, c)).join(' + ') : '—'
}

const Stat = ({ label, value, hint }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 180px', minWidth: 0 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{value}</Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Paper>
)

const WorkspaceList = ({ title, rows, dateLabel, empty }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 300px', minWidth: 0 }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
    {rows.length === 0 ? <Typography variant="body2" color="text.secondary">{empty}</Typography> : (
      <Stack spacing={1}>
        {rows.map((w) => (
          <Stack key={w.tenantId} direction="row" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>{w.name}</Typography>
              <Typography variant="caption" color="text.secondary">{w.planName} · {(STATUSES.find((x) => x.value === w.status) || {}).label || w.status}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{dateLabel} {day(w.date)}</Typography>
          </Stack>
        ))}
      </Stack>
    )}
  </Paper>
)

/** MRR, collections, conversion, and the workspaces that need a nudge. */
const OverviewTab = () => {
  const notify = useNotify()
  const overview = useQuery({ queryKey: ['platform', 'billing', 'overview'], queryFn: platformBillingAPI.overview, retry: false })
  const run = useMutation({ mutationFn: platformBillingAPI.runNotices, onSuccess: () => notify('Due billing notices sent') })
  const queryClient = useQueryClient()
  const reconcile = useMutation({
    mutationFn: platformBillingAPI.reconcile,
    onSuccess: (changes) => {
      notify(changes.length ? `Corrected ${changes.length}: ${changes.map((c) => `${c.workspace} (${c.what})`).join(', ')}` : 'All subscriptions match the payment providers')
      queryClient.invalidateQueries({ queryKey: ['platform', 'billing'] })
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const alert = useMutation({
    mutationFn: platformBillingAPI.testAlert,
    onSuccess: (d) => notify(d.message),
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  if (overview.isLoading) return <Loading />
  if (overview.error) return <Alert severity="error">{getErrorMessage(overview.error)}</Alert>
  const o = overview.data
  const rate = o.trialsOver90d ? Math.round((o.converted90d / o.trialsOver90d) * 100) : null
  return (
    <Stack spacing={3}>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
        <Stat label="Monthly recurring revenue" value={moneyList(o.mrr)} hint="Yearly plans count as 1/12; currencies aren't converted" />
        <Stat label="Collected, last 30 days" value={moneyList(o.collected30d)} hint="Online payments" />
        <Stat label="Paying workspaces" value={o.paying} hint={`${o.complimentary} free of charge`} />
        <Stat label="On a trial" value={o.trialing} hint={`${o.trialsEnded} trials ended without paying`} />
        <Stat label="Trial conversion" value={rate == null ? '—' : `${rate}%`} hint={`${o.converted90d} of ${o.trialsOver90d} new workspaces (90 days) pay`} />
      </Stack>
      {o.plans.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Paying</TableCell>
                <TableCell align="right">Monthly recurring revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {o.plans.map((p) => (
                <TableRow key={p.code}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="right">{p.paying}</TableCell>
                  <TableCell align="right">{moneyList(p.mrr)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
        <WorkspaceList title="Trials ending this week" rows={o.trialsEndingSoon} dateLabel="ends" empty="None" />
        <WorkspaceList title={`At risk (${o.pastDue} overdue, ${o.cancelling} cancelling)`} rows={o.atRisk} dateLabel="paid until" empty="None" />
        <WorkspaceList title="Recently lost" rows={o.recentlyLost} dateLabel="ended" empty="None" />
      </Stack>
      <Stack spacing={1}>
        <Box>
          <Button size="small" onClick={() => run.mutate()} disabled={run.isPending}>Send due billing notices now</Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            They go out every hour by themselves (trial ending, overdue, read-only, locked, limits); each only once.
          </Typography>
        </Box>
        <Box>
          <Button size="small" onClick={() => reconcile.mutate()} disabled={reconcile.isPending}>
            {reconcile.isPending ? 'Checking…' : 'Check payments with Razorpay / Stripe'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Runs every night too: fixes renewals or cancellations the app missed.
          </Typography>
        </Box>
        <Box>
          <Button size="small" onClick={() => alert.mutate()} disabled={alert.isPending}>Send a test error alert</Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Checks that error alerts reach your email or chat (ALERT_EMAILS / ALERT_WEBHOOK_URL).
          </Typography>
        </Box>
      </Stack>
    </Stack>
  )
}

// ------------------------------------------------------------------ workspaces

const WorkspacesTab = () => {
  const [editing, setEditing] = useState(null)
  const subs = useQuery({ queryKey: ['platform', 'billing', 'subscriptions'], queryFn: platformBillingAPI.subscriptions, retry: false })
  const plans = useQuery({ queryKey: ['platform', 'billing', 'plans'], queryFn: platformBillingAPI.plans, retry: false })
  if (subs.isLoading || plans.isLoading) return <Loading />
  if (subs.error) return <Alert severity="error">{getErrorMessage(subs.error)}</Alert>
  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Workspace</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Until</TableCell>
              <TableCell align="right">Members</TableCell>
              <TableCell align="right">Records</TableCell>
              <TableCell>Note</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {subs.data.map((s) => (
              <TableRow key={s.tenantId} hover>
                <TableCell sx={{ fontWeight: 500 }}>
                  {s.tenantName}
                  {s.tenantStatus !== 'ACTIVE' && <Chip size="small" label={s.tenantStatus.toLowerCase()} sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>
                  {s.planName}
                  {s.interval && <Typography component="span" variant="caption" color="text.secondary"> · {s.interval === 'YEAR' ? 'yearly' : 'monthly'}</Typography>}
                  {s.provider !== 'MANUAL' && <Typography component="span" variant="caption" color="text.secondary"> · {s.provider.charAt(0) + s.provider.slice(1).toLowerCase()}</Typography>}
                </TableCell>
                <TableCell><Chip size="small" color={PHASES[s.phase].color} label={PHASES[s.phase].label} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{s.phaseEndsAt ? day(s.phaseEndsAt) : s.paidUntil ? day(s.paidUntil) : 'No end'}</TableCell>
                <TableCell align="right">{s.members} / {s.seats}</TableCell>
                <TableCell align="right">{formatNumber(s.records)}</TableCell>
                <TableCell sx={{ maxWidth: 220, color: 'text.secondary' }}><Typography variant="body2" noWrap title={s.note || ''}>{s.note || ''}</Typography></TableCell>
                <TableCell align="right">
                  <Tooltip title="Change subscription">
                    <IconButton size="small" onClick={() => setEditing(s)} aria-label={`Change subscription of ${s.tenantName}`}><EditOutlinedIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {editing && <SubscriptionDialog sub={editing} plans={plans.data || []} onClose={() => setEditing(null)} />}
    </>
  )
}

/** Extend a trial, give a plan for free, record a bank-transfer payment, change plan or seats. */
const SubscriptionDialog = ({ sub, plans, onClose }) => {
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [form, setForm] = useState({
    planCode: sub.planCode,
    status: sub.status,
    interval: sub.interval || '',
    currency: sub.currency || '',
    extraSeats: String(sub.extraSeats),
    noEnd: !sub.paidUntil,
    paidUntil: sub.paidUntil ? sub.paidUntil.slice(0, 16) : toInput(addDays(new Date(), 30)),
    note: sub.note || '',
  })
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const extend = (fn) => {
    const current = new Date(form.paidUntil)
    const base = form.noEnd || Number.isNaN(current.getTime()) || current < new Date() ? new Date() : current
    setForm((f) => ({ ...f, noEnd: false, paidUntil: toInput(fn(base)) }))
  }
  const mutation = useMutation({
    mutationFn: () => platformBillingAPI.updateSubscription(sub.tenantId, {
      planCode: form.planCode,
      status: form.status,
      interval: form.interval || null,
      currency: form.currency || null,
      extraSeats: Number(form.extraSeats) || 0,
      paidUntil: form.noEnd ? null : form.paidUntil,
      note: form.note.trim() || null,
    }),
    onSuccess: (saved) => {
      notify(`${saved.tenantName}: ${saved.planName}, ${PHASES[saved.phase].label.toLowerCase()}`)
      queryClient.invalidateQueries({ queryKey: ['platform', 'billing'] })
      queryClient.invalidateQueries({ queryKey: [sub.tenantId, 'billing'] })
      onClose()
    },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); e.stopPropagation(); mutation.mutate() } }}>
      <DialogTitle>Subscription of {sub.tenantName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {mutation.error && <Alert severity="error">{errorText(mutation.error)}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select fullWidth label="Plan" value={form.planCode} onChange={set('planCode')}>
              {plans.map((p) => <MenuItem key={p.code} value={p.code}>{p.name}{!p.active && ' (not offered)'}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Status" value={form.status} onChange={set('status')}>
              {STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select fullWidth label="Billed" value={form.interval} onChange={set('interval')}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="MONTH">Monthly</MenuItem>
              <MenuItem value="YEAR">Yearly</MenuItem>
            </TextField>
            <TextField select fullWidth label="Currency" value={form.currency} onChange={set('currency')}>
              <MenuItem value="">—</MenuItem>
              {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField fullWidth type="number" label="Extra seats" value={form.extraSeats} onChange={set('extraSeats')} inputProps={{ min: 0, max: 10000 }} />
          </Stack>
          <Box>
            <FormControlLabel control={<Switch checked={form.noEnd} onChange={(e) => setForm((f) => ({ ...f, noEnd: e.target.checked }))} />}
              label="No end date (complimentary)" />
            {!form.noEnd && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 1 }}>
                <TextField type="datetime-local" label={form.status === 'TRIALING' ? 'Trial ends' : 'Paid until'} value={form.paidUntil}
                  onChange={set('paidUntil')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 230 }} />
                <Stack direction="row" spacing={1} sx={{ '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
                  <Button size="small" onClick={() => extend((d) => addDays(d, 14))}>+14 days</Button>
                  <Button size="small" onClick={() => extend((d) => addMonths(d, 1))}>+1 month</Button>
                  <Button size="small" onClick={() => extend((d) => addYears(d, 1))}>+1 year</Button>
                </Stack>
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              After this date a paid workspace gets 7 days of grace, then 30 days read-only, then it locks. A trial or a
              cancelled subscription goes read-only straight away. Extending the date restores access at once.
            </Typography>
          </Box>
          <TextField label="Note (only platform admins see it)" value={form.note} onChange={set('note')} inputProps={{ maxLength: 500 }}
            placeholder="e.g. Paid by bank transfer, invoice 2026-031" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={mutation.isPending}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

// ------------------------------------------------------------------ plans

const PlansTab = () => {
  const [editing, setEditing] = useState(null)
  const plans = useQuery({ queryKey: ['platform', 'billing', 'plans'], queryFn: platformBillingAPI.plans, retry: false })
  if (plans.isLoading) return <Loading />
  if (plans.error) return <Alert severity="error">{getErrorMessage(plans.error)}</Alert>
  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">Changes apply at once to every workspace on the plan.</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})}>New plan</Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Plan</TableCell>
              <TableCell align="right">Members</TableCell>
              <TableCell align="right">Records</TableCell>
              <TableCell align="right">Storage</TableCell>
              <TableCell align="right">Automations</TableCell>
              <TableCell align="right">Emails / day</TableCell>
              <TableCell>Features</TableCell>
              <TableCell align="right">Monthly</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.data.map((p) => (
              <TableRow key={p.code} hover sx={{ opacity: p.active ? 1 : 0.6 }}>
                <TableCell sx={{ fontWeight: 500 }}>
                  {p.name} <Typography component="span" variant="caption" color="text.secondary">{p.code}</Typography>
                  {!p.active && <Chip size="small" label="not offered" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell align="right">{p.includedMembers}</TableCell>
                <TableCell align="right">{formatNumber(p.maxRecords)}</TableCell>
                <TableCell align="right">{formatStorage(p.storageMb)}</TableCell>
                <TableCell align="right">{formatNumber(p.maxAutomations)}</TableCell>
                <TableCell align="right">{formatNumber(p.emailsPerDay)}</TableCell>
                <TableCell>{p.features.map((f) => FEATURE_LABELS[f] || f).join(', ') || '—'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {CURRENCIES.map((c) => p.prices?.[c]?.MONTH != null && formatMoney(p.prices[c].MONTH, c)).filter(Boolean).join(' · ') || '—'}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(p)} aria-label={`Edit ${p.name}`}><EditOutlinedIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {editing && <PlanDialog plan={editing} onClose={() => setEditing(null)} />}
    </>
  )
}

const limitText = (value) => (value == null ? '' : String(value))
const limitValue = (text) => (String(text).trim() === '' ? null : Number(text))
const money = (minor) => (minor == null ? '' : String(minor / 100))

/** Limits left blank = no limit; prices in rupees / dollars (stored in paise / cents). */
const PlanDialog = ({ plan, onClose }) => {
  const queryClient = useQueryClient()
  const notify = useNotify()
  const isNew = !plan.code
  const [form, setForm] = useState({
    code: plan.code || '',
    name: plan.name || '',
    description: plan.description || '',
    sortOrder: String(plan.sortOrder ?? 100),
    active: plan.active ?? true,
    includedMembers: String(plan.includedMembers ?? 5),
    maxRecords: limitText(plan.maxRecords),
    storageGb: plan.storageMb == null ? '' : String(+(plan.storageMb / 1024).toFixed(2)),
    maxAutomations: limitText(plan.maxAutomations),
    emailsPerDay: limitText(plan.emailsPerDay),
    features: plan.features || [],
    prices: Object.fromEntries(CURRENCIES.map((c) => [c, Object.fromEntries(PRICE_FIELDS.map((f) => [f.key, money(plan.prices?.[c]?.[f.key])]))])),
  })
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const setPrice = (currency, key) => (e) => setForm((f) => ({ ...f, prices: { ...f.prices, [currency]: { ...f.prices[currency], [key]: e.target.value } } }))
  const toggleFeature = (feature) => setForm((f) => ({
    ...f, features: f.features.includes(feature) ? f.features.filter((x) => x !== feature) : [...f.features, feature],
  }))

  const mutation = useMutation({
    mutationFn: () => {
      const prices = {}
      CURRENCIES.forEach((c) => {
        const amounts = {}
        PRICE_FIELDS.forEach(({ key }) => {
          const text = form.prices[c][key].trim()
          if (text !== '') amounts[key] = Math.round(Number(text) * 100)
        })
        if (Object.keys(amounts).length) prices[c] = amounts
      })
      return platformBillingAPI.savePlan(form.code.trim(), {
        name: form.name.trim(),
        description: form.description.trim() || null,
        sortOrder: Number(form.sortOrder) || 0,
        active: form.active,
        includedMembers: Number(form.includedMembers),
        maxRecords: limitValue(form.maxRecords),
        storageMb: form.storageGb.trim() === '' ? null : Math.round(Number(form.storageGb) * 1024),
        maxAutomations: limitValue(form.maxAutomations),
        emailsPerDay: limitValue(form.emailsPerDay),
        features: form.features,
        prices,
      })
    },
    onSuccess: (saved) => {
      notify(`${saved.name} saved`)
      queryClient.invalidateQueries({ queryKey: ['platform', 'billing'] })
      onClose()
    },
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); e.stopPropagation(); mutation.mutate() } }}>
      <DialogTitle>{isNew ? 'New plan' : `Edit ${plan.name}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {mutation.error && <Alert severity="error">{errorText(mutation.error)}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth required label="Name" value={form.name} onChange={set('name')} />
            <TextField fullWidth required label="Code" value={form.code} onChange={set('code')} disabled={!isNew}
              helperText={isNew ? 'Lowercase, e.g. pro; can’t be changed later' : undefined} />
          </Stack>
          <TextField label="Description" value={form.description} onChange={set('description')} inputProps={{ maxLength: 500 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField type="number" label="Order" value={form.sortOrder} onChange={set('sortOrder')} sx={{ width: { sm: 120 } }} />
            <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
              label="Offered to customers" />
          </Stack>
          <Typography variant="subtitle2">Limits <Typography component="span" variant="caption" color="text.secondary">(blank = no limit)</Typography></Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            <TextField required type="number" label="Members included" value={form.includedMembers} onChange={set('includedMembers')} inputProps={{ min: 1 }} />
            <TextField type="number" label="Records" value={form.maxRecords} onChange={set('maxRecords')} inputProps={{ min: 1 }} />
            <TextField type="number" label="Storage (GB)" value={form.storageGb} onChange={set('storageGb')} inputProps={{ min: 0.01, step: 'any' }} />
            <TextField type="number" label="Automations" value={form.maxAutomations} onChange={set('maxAutomations')} inputProps={{ min: 1 }} />
            <TextField type="number" label="Emails a day" value={form.emailsPerDay} onChange={set('emailsPerDay')} inputProps={{ min: 1 }} />
          </Box>
          <FormGroup row>
            {Object.entries(FEATURE_LABELS).map(([key, label]) => (
              <FormControlLabel key={key} control={<Checkbox checked={form.features.includes(key)} onChange={() => toggleFeature(key)} />} label={label} />
            ))}
          </FormGroup>
          <Typography variant="subtitle2">Prices <Typography component="span" variant="caption" color="text.secondary">(blank = not offered in that currency)</Typography></Typography>
          {CURRENCIES.map((c) => (
            <Box key={c} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
              {PRICE_FIELDS.map((f) => (
                <TextField key={f.key} type="number" size="small" label={`${c} · ${f.label}`} value={form.prices[c][f.key]}
                  onChange={setPrice(c, f.key)} inputProps={{ min: 0, step: 'any' }} />
              ))}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={mutation.isPending}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

// ------------------------------------------------------------------ invoicing

const SELLER_FIELDS = ['legalName', 'address', 'stateCode', 'gstin', 'email', 'invoicePrefix', 'creditNotePrefix', 'sacCode', 'lutNumber', 'footer']

/** The seller's GST details (a GSTIN switches GST and invoices on) and every invoice, with credit notes. */
const InvoicingTab = () => {
  const settings = useQuery({ queryKey: ['platform', 'billing', 'invoice-settings'], queryFn: platformBillingAPI.invoiceSettings, retry: false })
  const invoices = useQuery({ queryKey: ['platform', 'billing', 'invoices'], queryFn: platformBillingAPI.invoices, retry: false })
  const [crediting, setCrediting] = useState(null)
  const notify = useNotify()
  if (settings.isLoading) return <Loading />
  if (settings.error) return <Alert severity="error">{getErrorMessage(settings.error)}</Alert>
  const download = async (inv) => {
    try {
      downloadBlob(await platformBillingAPI.invoicePdf(inv.id), invoiceFileName(inv.number))
    } catch (e) {
      notify(getErrorMessage(e), 'error')
    }
  }
  return (
    <Stack spacing={3}>
      <SellerForm data={settings.data} />
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>Invoices and credit notes</Typography>
        {invoices.data?.length ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Number</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Workspace</TableCell>
                  <TableCell>Tax</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Emailed</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.data.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" onClick={() => download(inv)} sx={{ p: 0, minWidth: 0, textTransform: 'none' }}>{inv.number}</Button>
                      {inv.kind === 'CREDIT_NOTE' && <Typography variant="caption" color="text.secondary" component="div">against {inv.originalNumber}</Typography>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{day(inv.invoiceDate)}</TableCell>
                    <TableCell>{inv.tenantName}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {inv.supplyType === 'EXPORT' ? 'Export (0%)' : `${inv.supplyType === 'INTRA' ? 'CGST + SGST' : 'IGST'} ${formatRate(inv.taxRateBp)}`}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {inv.kind === 'CREDIT_NOTE' ? '−' : ''}{formatMoney(inv.total, inv.currency)}
                      {inv.credited > 0 && <Typography variant="caption" color="text.secondary" component="div">{formatMoney(inv.credited, inv.currency)} credited</Typography>}
                    </TableCell>
                    <TableCell>{inv.emailedOn ? day(inv.emailedOn) : '—'}</TableCell>
                    <TableCell align="right">
                      {inv.kind === 'INVOICE' && inv.credited < inv.total && (
                        <Button size="small" onClick={() => setCrediting(inv)} sx={{ whiteSpace: 'nowrap' }}>Credit note</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {settings.data.enabled ? 'No invoices yet. One is issued for every online payment.' : 'Invoices are issued once your GST details are saved above.'}
          </Typography>
        )}
      </Box>
      {crediting && <CreditNoteDialog invoice={crediting} onClose={() => setCrediting(null)} />}
    </Stack>
  )
}

const SellerForm = ({ data }) => {
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [form, setForm] = useState(() => ({
    ...Object.fromEntries(SELLER_FIELDS.map((k) => [k, data.seller[k] ?? ''])),
    gstRate: String(data.seller.gstRateBp / 100),
    pricesIncludeGst: data.seller.pricesIncludeGst,
  }))
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const save = useMutation({
    mutationFn: () => platformBillingAPI.saveInvoiceSettings({
      ...Object.fromEntries(SELLER_FIELDS.map((k) => [k, form[k].trim() || null])),
      gstRateBp: Math.round(Number(form.gstRate) * 100),
      pricesIncludeGst: form.pricesIncludeGst,
    }),
    onSuccess: (saved) => {
      notify(saved.enabled ? 'Saved: GST invoices are on' : 'Saved: GST invoices are off until a GSTIN is set')
      queryClient.invalidateQueries({ queryKey: ['platform', 'billing'] })
    },
  })
  return (
    <Paper variant="outlined" component="form" onSubmit={(e) => { e.preventDefault(); save.mutate() }} sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Your GST details</Typography>
        <Chip size="small" color={data.enabled ? 'success' : 'default'} label={data.enabled ? 'Invoices on' : 'Invoices off'} />
      </Stack>
      <Stack spacing={2}>
        {save.error && <Alert severity="error">{errorText(save.error)}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField fullWidth label="Legal name" value={form.legalName} onChange={set('legalName')} inputProps={{ maxLength: 200 }} />
          <TextField fullWidth label="GSTIN" value={form.gstin} onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))}
            inputProps={{ maxLength: 15 }} helperText="Saving a GSTIN switches GST and invoices on; clear it to switch them off" />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField fullWidth multiline minRows={2} label="Address" value={form.address} onChange={set('address')} inputProps={{ maxLength: 1000 }} />
          <TextField select fullWidth label="State" value={form.stateCode} onChange={set('stateCode')} helperText="Taken from the GSTIN if left empty">
            <MenuItem value="">—</MenuItem>
            {data.states.map((x) => <MenuItem key={x.code} value={x.code}>{x.name} ({x.code})</MenuItem>)}
          </TextField>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
          <TextField label="GST rate (%)" type="number" value={form.gstRate} onChange={set('gstRate')} inputProps={{ min: 0, max: 50, step: 'any' }} />
          <TextField label="SAC code" value={form.sacCode} onChange={set('sacCode')} inputProps={{ maxLength: 8 }} />
          <TextField label="Invoice prefix" value={form.invoicePrefix} onChange={(e) => setForm((f) => ({ ...f, invoicePrefix: e.target.value.toUpperCase() }))}
            inputProps={{ maxLength: 3 }} helperText={`${form.invoicePrefix || 'VEN'}/2026-27/0001`} />
          <TextField label="Credit note prefix" value={form.creditNotePrefix} onChange={(e) => setForm((f) => ({ ...f, creditNotePrefix: e.target.value.toUpperCase() }))}
            inputProps={{ maxLength: 3 }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField fullWidth label="Billing email (replies to invoices)" value={form.email} onChange={set('email')} inputProps={{ maxLength: 320 }} />
          <TextField fullWidth label="LUT number (exports)" value={form.lutNumber} onChange={set('lutNumber')} inputProps={{ maxLength: 50 }}
            helperText="Printed on invoices to customers abroad" />
        </Stack>
        <TextField label="Footer on invoices" value={form.footer} onChange={set('footer')} inputProps={{ maxLength: 500 }} />
        <FormControlLabel control={<Switch checked={form.pricesIncludeGst} onChange={(e) => setForm((f) => ({ ...f, pricesIncludeGst: e.target.checked }))} />}
          label={form.pricesIncludeGst ? 'Plan prices include GST' : 'GST is added on top of plan prices (customers in India)'} />
        <Typography variant="caption" color="text.secondary">
          Customers in your state pay CGST + SGST, other states IGST, customers abroad none (export of services). Changing these affects new
          checkouts and future invoices; subscriptions already running keep charging what they were set up with. Check the SAC code and
          rate with your accountant.
        </Typography>
        <Box><Button type="submit" variant="contained" disabled={save.isPending}>Save</Button></Box>
      </Stack>
    </Paper>
  )
}

/** After refunding in Razorpay / Stripe: a credit note for the amount, emailed to the customer. */
const CreditNoteDialog = ({ invoice, onClose }) => {
  const queryClient = useQueryClient()
  const notify = useNotify()
  const left = invoice.total - invoice.credited
  const [amount, setAmount] = useState(String(left / 100))
  const [reason, setReason] = useState('')
  const save = useMutation({
    mutationFn: () => platformBillingAPI.creditNote(invoice.id, { amount: Math.round(Number(amount) * 100), reason: reason.trim() || null }),
    onSuccess: (cn) => {
      notify(`Credit note ${cn.number} issued`)
      queryClient.invalidateQueries({ queryKey: ['platform', 'billing', 'invoices'] })
      onClose()
    },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); e.stopPropagation(); save.mutate() } }}>
      <DialogTitle>Credit note for {invoice.number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{errorText(save.error)}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Refund the money in {invoice.details.paymentProvider || 'the payment provider'}&apos;s dashboard first; this records it for GST.
            Up to {formatMoney(left, invoice.currency)} can be credited.
          </Typography>
          <TextField required type="number" label={`Amount incl. GST (${invoice.currency})`} value={amount} onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0.01, max: left / 100, step: 'any' }} />
          <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 500 }}
            placeholder="e.g. Refund for unused seats" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={save.isPending}>Issue credit note</Button>
      </DialogActions>
    </Dialog>
  )
}

// ------------------------------------------------------------------ coupons

const couponValueText = (c) => {
  const off = c.percentOff != null ? `${c.percentOff}% off` : `${formatMoney(c.amountOff, c.currency)} off`
  return `${off} ${c.durationMonths === 1 ? 'the first month' : `for ${c.durationMonths} months`}`
}

const CouponsTab = () => {
  const [editing, setEditing] = useState(null)
  const coupons = useQuery({ queryKey: ['platform', 'billing', 'coupons'], queryFn: platformBillingAPI.coupons, retry: false })
  if (coupons.isLoading) return <Loading />
  if (coupons.error) return <Alert severity="error">{getErrorMessage(coupons.error)}</Alert>
  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Customers enter a code when choosing a plan. The discount is given as free time before the first charge (100% off the first month = a
          free month), once per workspace.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})} sx={{ flexShrink: 0 }}>New coupon</Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Used</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.data.length === 0 && (
              <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No coupons yet</Typography></TableCell></TableRow>
            )}
            {coupons.data.map((c) => (
              <TableRow key={c.code} hover sx={{ opacity: c.active ? 1 : 0.6 }}>
                <TableCell sx={{ fontWeight: 500 }}>
                  {c.code}
                  {!c.active && <Chip size="small" label="off" sx={{ ml: 1 }} />}
                  {c.description && <Typography variant="caption" color="text.secondary" component="div">{c.description}</Typography>}
                </TableCell>
                <TableCell>{couponValueText(c)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.expiresAt ? day(c.expiresAt) : 'Never'}</TableCell>
                <TableCell align="right">{c.redemptions}{c.maxRedemptions != null && ` / ${c.maxRedemptions}`}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(c)} aria-label={`Edit ${c.code}`}><EditOutlinedIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {editing && <CouponDialog coupon={editing} onClose={() => setEditing(null)} />}
    </>
  )
}

const CouponDialog = ({ coupon, onClose }) => {
  const queryClient = useQueryClient()
  const notify = useNotify()
  const isNew = !coupon.code
  const [form, setForm] = useState({
    code: coupon.code || '',
    description: coupon.description || '',
    kind: coupon.amountOff != null ? 'AMOUNT' : 'PERCENT',
    percentOff: coupon.percentOff != null ? String(coupon.percentOff) : '100',
    amountOff: coupon.amountOff != null ? String(coupon.amountOff / 100) : '',
    currency: coupon.currency || 'INR',
    durationMonths: String(coupon.durationMonths ?? 1),
    expiresAt: coupon.expiresAt ? coupon.expiresAt.slice(0, 16) : '',
    maxRedemptions: coupon.maxRedemptions != null ? String(coupon.maxRedemptions) : '',
    active: coupon.active ?? true,
  })
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const save = useMutation({
    mutationFn: () => platformBillingAPI.saveCoupon(form.code.trim(), {
      description: form.description.trim() || null,
      percentOff: form.kind === 'PERCENT' ? Number(form.percentOff) : null,
      amountOff: form.kind === 'AMOUNT' ? Math.round(Number(form.amountOff) * 100) : null,
      currency: form.kind === 'AMOUNT' ? form.currency : null,
      durationMonths: Number(form.durationMonths) || 1,
      expiresAt: form.expiresAt || null,
      maxRedemptions: form.maxRedemptions.trim() === '' ? null : Number(form.maxRedemptions),
      active: form.active,
    }),
    onSuccess: (saved) => {
      notify(`${saved.code} saved`)
      queryClient.invalidateQueries({ queryKey: ['platform', 'billing', 'coupons'] })
      onClose()
    },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); e.stopPropagation(); save.mutate() } }}>
      <DialogTitle>{isNew ? 'New coupon' : `Edit ${coupon.code}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{errorText(save.error)}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField required fullWidth label="Code" value={form.code} disabled={!isNew}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} inputProps={{ maxLength: 40 }}
              helperText={isNew ? 'e.g. LAUNCH50; letters, digits, - or _' : undefined} />
            <TextField fullWidth label="Description (only you see it)" value={form.description} onChange={set('description')} inputProps={{ maxLength: 200 }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label="Discount" value={form.kind} onChange={set('kind')} sx={{ minWidth: 160 }}>
              <MenuItem value="PERCENT">Percentage</MenuItem>
              <MenuItem value="AMOUNT">Fixed amount</MenuItem>
            </TextField>
            {form.kind === 'PERCENT' ? (
              <TextField required type="number" label="% off" value={form.percentOff} onChange={set('percentOff')} inputProps={{ min: 1, max: 100 }} />
            ) : (
              <>
                <TextField required type="number" label="Off each month" value={form.amountOff} onChange={set('amountOff')} inputProps={{ min: 0.01, step: 'any' }} />
                <TextField select label="Currency" value={form.currency} onChange={set('currency')} sx={{ minWidth: 100 }}>
                  {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </>
            )}
            <TextField required type="number" label="Months" value={form.durationMonths} onChange={set('durationMonths')}
              inputProps={{ min: 1, max: 36 }} helperText="1 = first month" />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth type="datetime-local" label="Expires (optional)" value={form.expiresAt} onChange={set('expiresAt')} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth type="number" label="Usage limit (optional)" value={form.maxRedemptions} onChange={set('maxRedemptions')}
              inputProps={{ min: 1 }} helperText="Workspaces that can use it in total" />
          </Stack>
          <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />} label="Can be used" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={save.isPending}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

export default PlatformBillingPage
